import { useCallback, useEffect, useState } from 'react'
import type { IslandProps } from '../../registry'
import { EmptyState, Skeleton } from '../../../shared/ui'
import {
  fetchToolConnections,
  toolName,
  type ToolConnection,
} from '../../../lib/outreachConnectors'
import { fetchMyWorkspaces, type WorkspaceSummary } from '../../../lib/workspace'
import { ToolsSection } from './ToolsSection'
import { HistoryImport } from './HistoryImport'
import { TOOLS_CSS } from './toolsStyles'

/**
 * Settings > Outreach tools (docs/proposals/outreach-connectors.md).
 *
 * For the business that is open: connect sending tools (Instantly,
 * Benchmark Email) by pasting an API key, see what each has synced, and
 * import past outreach from a CSV. Tools of the user's other businesses are
 * listed underneath, read-only.
 */
export default function OutreachToolsIsland(_props: IslandProps) {
  const [business, setBusiness] = useState<WorkspaceSummary | null>(null)
  const [connections, setConnections] = useState<ToolConnection[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState('')
  const [tick, setTick] = useState(0)
  const refetch = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [wss, conns] = await Promise.all([fetchMyWorkspaces(), fetchToolConnections()])
        if (cancelled) return
        setBusiness(wss.find((w) => w.is_current) ?? wss[0] ?? null)
        setConnections(conns)
        setStatus('ready')
        setError('')
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Could not load your outreach tools.')
        setStatus('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tick])

  const syncing = connections.some((c) => c.status === 'syncing')
  useEffect(() => {
    if (!syncing) return
    const t = setInterval(refetch, 10_000)
    return () => clearInterval(t)
  }, [syncing, refetch])

  if (status === 'loading') {
    return (
      <div aria-busy="true" aria-label="Loading">
        <Skeleton width="40" />
        <Skeleton />
      </div>
    )
  }
  if (status === 'error' || !business) {
    return (
      <EmptyState
        role="alert"
        title="Could not load your outreach tools"
        message={error || 'Something went wrong.'}
        cta={{ label: 'Retry', onClick: refetch }}
      />
    )
  }

  const here = connections.filter((c) => c.workspace_id === business.id)
  const elsewhere = connections.filter((c) => c.workspace_id !== business.id)
  const canManage = business.role === 'owner' || business.role === 'admin'

  return (
    <div className="ot-page">
      <style>{TOOLS_CSS}</style>

      <section className="ot-block" aria-labelledby="ot-tools-h">
        <h3 className="ot-h" id="ot-tools-h">
          Sending tools · {business.name}
        </h3>
        <p className="ot-sub">
          Paste an API key. Every person you emailed becomes a contact here, and each send, reply,
          open, click and bounce shows on their timeline. Syncs every 30 minutes.
        </p>
        <ToolsSection
          workspaceId={business.id}
          canManage={canManage}
          connections={here}
          onChanged={refetch}
        />
      </section>

      <section className="ot-block" aria-labelledby="ot-import-h">
        <h3 className="ot-h" id="ot-import-h">
          Import outreach history
        </h3>
        <HistoryImport businessName={business.name} />
      </section>

      {elsewhere.length > 0 && (
        <section className="ot-block" aria-labelledby="ot-other-h">
          <h3 className="ot-h" id="ot-other-h">
            Other businesses
          </h3>
          <ul className="ot-list">
            {elsewhere.map((c) => (
              <li key={c.id} className="ot-dim">
                {c.workspace_name}: {toolName(c)} · {c.activities.toLocaleString()} activities
              </li>
            ))}
          </ul>
          <p className="ot-sub">Switch to that business to manage its tools.</p>
        </section>
      )}
    </div>
  )
}
